import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUserPlus, faEllipsis, faMagnifyingGlass, faTriangleExclamation,
  faUserSlash, faUserCheck, faUserTie, faArrowRightArrowLeft, faChevronDown,
  faEye, faEyeSlash,
} from "@fortawesome/free-solid-svg-icons";
import ModalHeader from "./ModalHeader";
import api from "../services/api";
import styles from "./OrganizationModal.module.css";
import { describeOrgBilling, formatDateBR, formatPlanPrice } from "../utils/orgBilling";
import { useBodyScrollLock } from "../hooks/useBodyScrollLock";
import { useFocusTrap } from "../hooks/useFocusTrap";
import { useToast } from "./ToastProvider";
import PasswordRequirementsChecklist, { isPasswordStrong } from "./PasswordRequirementsChecklist";

// "Minha academia" (F1 do suporte multi-professor). Consome
// /organizations/me (app/api/organizations.py). Dono e gestor administram
// professores e a carteira de alunos; professor comum da academia só consulta
// a lista de professores. Wireframe validado em 26/09/2026.

const ROLE_LABEL = { owner: "Dono", manager: "Gestor", professor: "Professor" };
const TABS = [
  { key: "professors", label: "Professores" },
  { key: "students", label: "Alunos" },
  { key: "billing", label: "Assinatura" },
];
const PAGE_SIZE = 50;

function initials(name = "") {
  return name.split(" ").filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join("");
}

function errMsg(err, fallback) {
  const detail = err?.response?.data?.detail;
  if (detail && typeof detail === "object") return detail.message || fallback;
  return detail || fallback;
}

function fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric" });
  } catch { return ""; }
}

function plural(n, one, many) {
  return `${n} ${n === 1 ? one : many}`;
}

function Avatar({ user, apiUrl, off }) {
  if (user?.avatar_url) {
    const src = user.avatar_url.startsWith("http")
      ? user.avatar_url.replace(/=s\d+-c/, "=s40-c")
      : `${apiUrl}/${user.avatar_url}`;
    return <img src={src} alt="" referrerPolicy="no-referrer" loading="lazy" className={`${styles.avatar} ${off ? styles.avatarOff : ""}`} />;
  }
  return <div className={`${styles.avatar} ${off ? styles.avatarOff : ""}`} aria-hidden="true">{initials(user?.name)}</div>;
}

// Folha sobre o modal (inferior no celular, centralizada no desktop). Usa o
// mesmo useFocusTrap do ModalHeader: por ser a camada do topo, Escape fecha
// só a folha, não o modal inteiro (ver hooks/modalLayers.js).
export function Sheet({ title, onClose, busy, children, labelId }) {
  const ref = useRef(null);
  useFocusTrap(ref, true, busy ? undefined : onClose);
  return (
    <div className={styles.sheetOverlay} onClick={busy ? undefined : onClose} role="presentation">
      <div
        ref={ref}
        className={styles.sheet}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelId}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={styles.grab} aria-hidden="true" />
        <h3 id={labelId} className={styles.sheetTitle}>{title}</h3>
        {children}
      </div>
    </div>
  );
}

// ── Adicionar professor (cadastro direto, sem convite) ──────────────────────
function CreateProfessorSheet({ isOwner, onClose, onDone }) {
  const toast = useToast();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [role, setRole] = useState("professor");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const canSubmit = name.trim().length >= 2 && email.trim() && isPasswordStrong(password);

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    try {
      const res = await api.post("/organizations/me/professors", {
        name: name.trim(), email: email.trim(), password, org_role: role,
      });
      toast.success(res.data?.message || "Professor cadastrado.");
      onDone();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      // 422 do pydantic vem como lista; a regra de senha já aparece no checklist
      setError(Array.isArray(detail) ? "Confira os dados e a senha." : errMsg(err, "Não foi possível cadastrar. Tente novamente."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet title="Adicionar professor" onClose={onClose} busy={busy} labelId="org-create-title">
      <form className={styles.form} onSubmit={submit}>
        <div className={styles.field}>
          <label htmlFor="org-create-name">Nome</label>
          <input id="org-create-name" className={styles.input} value={name} onChange={(e) => setName(e.target.value)} required minLength={2} autoComplete="off" placeholder="Ex.: Juliana Castro" />
        </div>
        <div className={styles.field}>
          <label htmlFor="org-create-email">Email</label>
          <input id="org-create-email" className={styles.input} type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="off" placeholder="juliana@email.com" />
        </div>
        <div className={styles.field}>
          <label htmlFor="org-create-password">Senha inicial</label>
          <div className={styles.passwordWrap}>
            <input
              id="org-create-password"
              className={styles.input}
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="new-password"
              aria-describedby="org-create-password-hint"
            />
            <button
              type="button"
              className={styles.passwordToggle}
              onClick={() => setShowPassword((v) => !v)}
              aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
              aria-pressed={showPassword}
            >
              <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
            </button>
          </div>
          <PasswordRequirementsChecklist password={password} />
        </div>
        {isOwner && (
          <fieldset className={styles.fieldset}>
            <legend>Papel na academia</legend>
            <div className={styles.seg}>
              {[
                { key: "professor", label: "Professor", hint: "Atende os alunos" },
                { key: "manager", label: "Gestor", hint: "Também administra" },
              ].map((o) => (
                <label key={o.key} className={`${styles.radio} ${role === o.key ? styles.radioOn : ""}`}>
                  <input type="radio" name="org-create-role" value={o.key} checked={role === o.key} onChange={() => setRole(o.key)} />
                  <span>
                    <span className={styles.radioLabel}>{o.label}</span>
                    <span className={styles.hint}>{o.hint}</span>
                  </span>
                </label>
              ))}
            </div>
          </fieldset>
        )}
        <p id="org-create-password-hint" className={styles.sheetText}>Passe o email e a senha para o professor. No primeiro acesso, ele cria a própria senha.</p>
        <p className={styles.hint}>Se a pessoa já usa o TreinoNexo, fale com o suporte para trazê-la para a academia.</p>
        {error && <div className={styles.error} role="alert"><FontAwesomeIcon icon={faTriangleExclamation} /> {error}</div>}
        <div className={styles.sheetActions}>
          <button type="button" className={styles.btn} onClick={onClose} disabled={busy}>Cancelar</button>
          <button type="submit" className={`${styles.btn} ${styles.btnPrimary}`} disabled={busy || !canSubmit}>
            {busy ? "Cadastrando..." : "Cadastrar professor"}
          </button>
        </div>
      </form>
    </Sheet>
  );
}

// ── Ações de um professor ───────────────────────────────────────────────────
function ProfessorActionsSheet({ prof, isOwner, onClose, onDeactivate, onChanged }) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);

  const patch = async (body, okMsg) => {
    setBusy(true);
    try {
      await api.patch(`/organizations/me/professors/${prof.id}`, body);
      toast.success(okMsg);
      onChanged();
    } catch (err) {
      toast.error(errMsg(err, "Não foi possível salvar. Tente novamente."));
      setBusy(false);
    }
  };

  const nextRole = prof.org_role === "manager" ? "professor" : "manager";

  return (
    <Sheet title={prof.name} onClose={onClose} busy={busy} labelId="org-actions-title">
      <div className={styles.actionList}>
        {prof.is_active ? (
          <>
            {isOwner && (
              <button type="button" className={styles.actionItem} disabled={busy}
                onClick={() => patch({ org_role: nextRole }, nextRole === "manager" ? `${prof.name} agora é gestor.` : `${prof.name} agora é professor.`)}>
                <FontAwesomeIcon icon={faUserTie} />
                <span>{nextRole === "manager" ? "Tornar gestor" : "Tornar professor"}</span>
              </button>
            )}
            <button type="button" className={`${styles.actionItem} ${styles.actionDanger}`} disabled={busy} onClick={onDeactivate}>
              <FontAwesomeIcon icon={faUserSlash} />
              <span>Inativar professor</span>
            </button>
          </>
        ) : (
          <button type="button" className={styles.actionItem} disabled={busy}
            onClick={() => patch({ is_active: true }, `${prof.name} foi reativado.`)}>
            <FontAwesomeIcon icon={faUserCheck} />
            <span>Reativar professor</span>
          </button>
        )}
      </div>
      <div className={styles.sheetActions}>
        <button type="button" className={styles.btn} onClick={onClose} disabled={busy}>Fechar</button>
      </div>
    </Sheet>
  );
}

// ── Inativar professor (confirmação com o efeito real) ──────────────────────
function DeactivateSheet({ prof, onClose, onDone }) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);

  const confirm = async () => {
    setBusy(true);
    try {
      const res = await api.patch(`/organizations/me/professors/${prof.id}`, { is_active: false });
      const moved = res.data?.students_moved_to_queue ?? 0;
      toast.success(moved
        ? `${prof.name} foi inativado. ${plural(moved, "aluno foi", "alunos foram")} para a fila Sem professor.`
        : `${prof.name} foi inativado.`);
      onDone();
    } catch (err) {
      toast.error(errMsg(err, "Não foi possível inativar. Tente novamente."));
      setBusy(false);
    }
  };

  return (
    <Sheet title={`Inativar ${prof.name}?`} onClose={onClose} busy={busy} labelId="org-deactivate-title">
      <ul className={styles.impact}>
        <li><span className={styles.impactKey}>{prof.students_count}</span><span>{prof.students_count === 1 ? "aluno dele vai" : "alunos dele vão"} para a fila Sem professor.</span></li>
        <li><span className={styles.impactKey}><FontAwesomeIcon icon={faUserCheck} /></span><span>Treinos e planos continuam valendo e podem ser editados pelos outros professores.</span></li>
        <li><span className={styles.impactKey}><FontAwesomeIcon icon={faUserSlash} /></span><span>{prof.name} perde o acesso ao app.</span></li>
      </ul>
      <p className={styles.sheetText}>Você pode reativar depois. Os alunos não voltam sozinhos: a redistribuição fica com o gestor.</p>
      <div className={styles.sheetActions}>
        <button type="button" className={styles.btn} onClick={onClose} disabled={busy}>Cancelar</button>
        <button type="button" className={`${styles.btn} ${styles.btnDanger}`} onClick={confirm} disabled={busy}>
          {busy ? "Inativando..." : "Inativar professor"}
        </button>
      </div>
    </Sheet>
  );
}

// ── Trocar / atribuir responsável ───────────────────────────────────────────
function TransferSheet({ student, professors, meId, onClose, onDone }) {
  const toast = useToast();
  const options = professors.filter((p) => p.is_active);
  const [target, setTarget] = useState(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get(`/organizations/me/students/${student.id}/transfers`)
      .then((r) => { if (alive) setHistory(r.data || []); })
      .catch(() => { if (alive) setHistory([]); });
    return () => { alive = false; };
  }, [student.id]);

  const submit = async (e) => {
    e.preventDefault();
    if (!target) return;
    setBusy(true);
    try {
      const res = await api.post(`/organizations/me/students/${student.id}/transfer`, {
        to_professor_id: target,
        reason: reason.trim() || null,
      });
      toast.success(`${student.name} agora está com ${res.data?.professor_name || "o novo professor"}.`);
      onDone();
    } catch (err) {
      toast.error(errMsg(err, "Não foi possível trocar o responsável. Tente novamente."));
      setBusy(false);
    }
  };

  const hasResp = !!student.professor_id;

  return (
    <Sheet title={hasResp ? "Trocar responsável" : "Atribuir professor"} onClose={onClose} busy={busy} labelId="org-transfer-title">
      <form className={styles.form} onSubmit={submit}>
        <p className={styles.sheetText}>
          <strong className={styles.strong}>{student.name}</strong>
          {" · "}
          {hasResp ? `responsável atual: ${student.professor_name}` : "sem professor responsável"}
        </p>
        <fieldset className={styles.fieldset}>
          <legend>Novo responsável</legend>
          <div className={styles.radios}>
            {options.map((p) => {
              const current = p.id === student.professor_id;
              return (
                <label key={p.id} className={`${styles.radio} ${target === p.id ? styles.radioOn : ""} ${current ? styles.radioDisabled : ""}`}>
                  <input type="radio" name="org-transfer-target" value={p.id} checked={target === p.id} disabled={current} onChange={() => setTarget(p.id)} />
                  <span>
                    <span className={styles.radioLabel}>{p.name}{p.id === meId ? " (você)" : ""}</span>
                    <span className={styles.hint}>{current ? "Responsável atual" : plural(p.students_count, "aluno", "alunos")}</span>
                  </span>
                </label>
              );
            })}
          </div>
        </fieldset>
        <div className={styles.field}>
          <label htmlFor="org-transfer-reason">Motivo (opcional)</label>
          <textarea id="org-transfer-reason" className={`${styles.input} ${styles.textarea}`} value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} placeholder="Ex.: mudou para o horário da noite" />
        </div>
        <p className={styles.hint}>O treino atual continua valendo. O novo responsável passa a receber os avisos e os reports deste aluno.</p>
        {history && history.length > 0 && (
          <div className={styles.history}>
            <span className={styles.sectionLabel}>Histórico</span>
            <ol className={styles.historyList}>
              {history.slice(0, 5).map((h) => (
                <li key={h.id}>
                  <strong className={styles.strong}>{fmtDate(h.created_at)}</strong>
                  {" · "}
                  {h.from_professor_name || "Sem professor"} para {h.to_professor_name || "Sem professor"}
                  {h.by_user_name ? `, por ${h.by_user_name}` : ""}
                  {h.reason ? ` · ${h.reason}` : ""}
                </li>
              ))}
            </ol>
          </div>
        )}
        <div className={styles.sheetActions}>
          <button type="button" className={styles.btn} onClick={onClose} disabled={busy}>Cancelar</button>
          <button type="submit" className={`${styles.btn} ${styles.btnPrimary}`} disabled={busy || !target}>
            {busy ? "Salvando..." : hasResp ? "Trocar responsável" : "Atribuir professor"}
          </button>
        </div>
      </form>
    </Sheet>
  );
}

// ── Modal principal ─────────────────────────────────────────────────────────
export default function OrganizationModal({ open, onClose, me, apiUrl, onOpenPayments }) {
  useBodyScrollLock(open);
  const toast = useToast();
  const isManager = me?.org_role === "owner" || me?.org_role === "manager";
  const isOwner = me?.org_role === "owner";

  const [tab, setTab] = useState("professors");
  const [org, setOrg] = useState(null);
  const [professors, setProfessors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInactive, setShowInactive] = useState(false);

  const [students, setStudents] = useState([]);
  const [studentsTotal, setStudentsTotal] = useState(0);
  const [studentsLoading, setStudentsLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [unassigned, setUnassigned] = useState(false);
  const [profFilter, setProfFilter] = useState("");

  const [createOpen, setCreateOpen] = useState(false);
  const [actionsFor, setActionsFor] = useState(null);
  const [deactivateFor, setDeactivateFor] = useState(null);
  const [transferFor, setTransferFor] = useState(null);

  const loadOverview = useCallback(async () => {
    try {
      const [orgRes, profRes] = await Promise.all([
        api.get("/organizations/me"),
        api.get("/organizations/me/professors"),
      ]);
      setOrg(orgRes.data);
      setProfessors(profRes.data || []);
    } catch (err) {
      toast.error(errMsg(err, "Não foi possível carregar a academia."));
    } finally {
      setLoading(false);
    }
  }, [toast]);

  const loadStudents = useCallback(async (offset = 0) => {
    setStudentsLoading(true);
    try {
      const params = { limit: PAGE_SIZE, offset };
      if (debouncedSearch.trim()) params.q = debouncedSearch.trim();
      if (unassigned) params.unassigned = true;
      else if (profFilter) params.professor_id = profFilter;
      const res = await api.get("/organizations/me/students", { params });
      const rows = res.data || [];
      setStudents((prev) => (offset ? [...prev, ...rows] : rows));
      setStudentsTotal(parseInt(res.headers?.["x-total-count"] ?? rows.length, 10));
    } catch (err) {
      toast.error(errMsg(err, "Não foi possível carregar os alunos."));
    } finally {
      setStudentsLoading(false);
    }
  }, [debouncedSearch, unassigned, profFilter, toast]);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    loadOverview();
  }, [open, loadOverview]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    if (open && isManager && tab === "students") loadStudents(0);
  }, [open, isManager, tab, loadStudents]);

  const activeProfs = useMemo(() => professors.filter((p) => p.is_active), [professors]);
  const inactiveProfs = useMemo(() => professors.filter((p) => !p.is_active), [professors]);

  const canManageProf = (p) =>
    isManager && p.id !== me?.id && p.org_role !== "owner" && (p.org_role !== "manager" || isOwner);

  const refreshAll = () => {
    loadOverview();
    if (tab === "students") loadStudents(0);
  };

  const openQueue = () => {
    setUnassigned(true);
    setProfFilter("");
    setTab("students");
  };

  const onTabKey = (e) => {
    if (e.key === "ArrowRight" || e.key === "ArrowLeft") {
      e.preventDefault();
      const i = TABS.findIndex((t) => t.key === tab);
      const next = TABS[(i + (e.key === "ArrowRight" ? 1 : TABS.length - 1)) % TABS.length].key;
      setTab(next);
      document.getElementById(`org-tab-${next}`)?.focus();
    }
  };

  if (!open) return null;

  const queue = org?.students_unassigned ?? 0;
  // Cobrança da academia (plano por academia): só dono e gestores veem. A
  // aba Assinatura mostra sempre (inclusive cortesia); o alerta acima das
  // abas só aparece em carência ou bloqueio. Ver utils/orgBilling.js.
  const billing = org?.billing;
  const billingInfo = isManager ? describeOrgBilling(billing) : null;
  const billingTone = billingInfo
    ? { ok: styles.billingOk, warn: styles.billingWarn, danger: styles.billingDanger }[billingInfo.tone] || ""
    : "";
  const billingAlert = billingInfo && billingInfo.tone !== "ok" && tab !== "billing";
  const payLabel = billingInfo?.tone === "ok"
    ? "Ver assinatura"
    : billing?.paid_through ? "Regularizar assinatura" : "Assinar plano da academia";

  const profRow = (p) => (
    <li key={p.id} className={styles.row}>
      <Avatar user={p} apiUrl={apiUrl} off={!p.is_active} />
      <div className={styles.grow}>
        <div className={styles.name}>{p.name}{p.id === me?.id ? " (você)" : ""}</div>
        <div className={styles.meta}>
          <span>{ROLE_LABEL[p.org_role] || "Professor"}</span>
          <span aria-hidden="true">·</span>
          <span>{plural(p.students_count, "aluno", "alunos")}</span>
          {p.awaiting_first_access && p.is_active && <span className={`${styles.pill} ${styles.pillInfo}`}>Aguardando primeiro acesso</span>}
          {!p.is_active && <span className={`${styles.pill} ${styles.pillOff}`}>Inativo</span>}
        </div>
      </div>
      {canManageProf(p) && (
        <button type="button" className={styles.moreBtn} aria-label={`Ações para ${p.name}`} onClick={() => setActionsFor(p)}>
          <FontAwesomeIcon icon={faEllipsis} />
        </button>
      )}
    </li>
  );

  return (
    <div className={styles.overlay} role="presentation">
      <div className={styles.modal} role="dialog" aria-modal="true" aria-labelledby="org-modal-title">
        <ModalHeader onClose={onClose} />

        <div className={styles.content}>
          <div className={styles.titleRow}>
            <div>
              <h2 id="org-modal-title" className={styles.title}>Minha academia</h2>
              <div className={styles.sub}>{org?.name || " "}</div>
            </div>
            {me?.org_role && (
              <span className={`${styles.roleChip} ${isOwner ? styles.roleChipOwner : ""}`}>{ROLE_LABEL[me.org_role]}</span>
            )}
          </div>

          <div className={styles.stats}>
            <div className={styles.stat}><b>{org?.professors_active ?? "-"}</b><span>{org?.professors_active === 1 ? "professor" : "professores"}</span></div>
            <div className={styles.stat}><b>{org?.students_active ?? "-"}</b><span>alunos ativos</span></div>
            {isManager ? (
              <button type="button" className={`${styles.stat} ${queue > 0 ? styles.statWarn : ""}`} onClick={openQueue} aria-label={`${queue} alunos sem professor. Ver fila`}>
                <b>{queue}</b><span>sem professor</span>
              </button>
            ) : (
              <div className={`${styles.stat} ${queue > 0 ? styles.statWarn : ""}`}><b>{queue}</b><span>sem professor</span></div>
            )}
          </div>

          {billingAlert && (
            <section className={`${styles.billing} ${billingTone}`} aria-label="Alerta da assinatura">
              <div className={styles.grow}>
                <div className={styles.billingTitle}>{billingInfo.title}</div>
                <div className={styles.billingDetail}>{billingInfo.detail}</div>
              </div>
              <button type="button" className={styles.btn} onClick={() => setTab("billing")}>Ver assinatura</button>
            </section>
          )}

          {isManager && (
            <div className={styles.tabs} role="tablist" aria-label="Seções da academia" onKeyDown={onTabKey}>
              {TABS.map((t) => (
                <button
                  key={t.key}
                  id={`org-tab-${t.key}`}
                  type="button"
                  role="tab"
                  aria-selected={tab === t.key}
                  aria-controls={`org-panel-${t.key}`}
                  tabIndex={tab === t.key ? 0 : -1}
                  className={`${styles.tab} ${tab === t.key ? styles.tabOn : ""}`}
                  onClick={() => setTab(t.key)}
                >
                  {t.label}
                </button>
              ))}
            </div>
          )}

          {loading ? (
            <div className={styles.empty}>Carregando...</div>
          ) : tab === "billing" && isManager ? (
            <div id="org-panel-billing" role="tabpanel" aria-labelledby="org-tab-billing" className={styles.panel}>
              <section className={`${styles.billing} ${billingTone}`} aria-label="Situação da assinatura">
                <div className={styles.grow}>
                  <div className={styles.billingTitle}>{billingInfo ? billingInfo.title : "Cortesia"}</div>
                  <div className={styles.billingDetail}>
                    {billingInfo
                      ? billingInfo.detail
                      : "A academia está sem cobrança no momento. Quando o plano for definido com a equipe TreinoNexo, ele aparece aqui."}
                  </div>
                </div>
              </section>

              <dl className={styles.billingFacts}>
                <div>
                  <dt>Plano da academia</dt>
                  <dd>{billing?.plan ? formatPlanPrice(billing.plan) : "Não definido"}</dd>
                </div>
                {billing?.paid_until && (
                  <div>
                    <dt>Contrato pago até</dt>
                    <dd>{formatDateBR(billing.paid_until)}</dd>
                  </div>
                )}
                {billing?.subscription && (
                  <div>
                    <dt>Assinatura no app</dt>
                    <dd>
                      {billing.subscription.status === "active" ? "Ativa" : billing.subscription.status === "canceled" ? "Cancelada" : "Em processamento"}
                      {billing.subscription.current_period_end && `, próximo vencimento em ${formatDateBR(billing.subscription.current_period_end)}`}
                    </dd>
                  </div>
                )}
              </dl>

              {isOwner && billing?.plan && onOpenPayments ? (
                <button
                  type="button"
                  className={`${styles.btn} ${styles.btnBlock} ${billingInfo?.tone === "ok" ? "" : styles.btnPrimary}`}
                  onClick={onOpenPayments}
                >
                  {payLabel}
                </button>
              ) : !isOwner && billing?.plan ? (
                <p className={styles.hint}>Quem assina é o dono da academia.</p>
              ) : null}
            </div>
          ) : tab === "professors" || !isManager ? (
            <div id="org-panel-professors" role={isManager ? "tabpanel" : undefined} aria-labelledby={isManager ? "org-tab-professors" : undefined} className={styles.panel}>
              {isManager && (
                <button type="button" className={`${styles.btn} ${styles.btnPrimary} ${styles.btnBlock}`} onClick={() => setCreateOpen(true)}>
                  <FontAwesomeIcon icon={faUserPlus} /> Adicionar professor
                </button>
              )}
              <ul className={styles.list}>{activeProfs.map(profRow)}</ul>
              {inactiveProfs.length > 0 && (
                <>
                  <button type="button" className={styles.collapse} aria-expanded={showInactive} onClick={() => setShowInactive((v) => !v)}>
                    <span>Inativos ({inactiveProfs.length})</span>
                    <FontAwesomeIcon icon={faChevronDown} className={showInactive ? styles.chevronUp : ""} />
                  </button>
                  {showInactive && <ul className={styles.list}>{inactiveProfs.map(profRow)}</ul>}
                </>
              )}
            </div>
          ) : (
            <div id="org-panel-students" role="tabpanel" aria-labelledby="org-tab-students" className={styles.panel}>
              {queue > 0 && !unassigned && (
                <div className={styles.banner}>
                  <b>{queue}</b>
                  <span className={styles.grow}>{queue === 1 ? "aluno está" : "alunos estão"} sem professor responsável.</span>
                  <button type="button" className={styles.linkBtn} onClick={openQueue}>Ver fila</button>
                </div>
              )}
              <div className={styles.search}>
                <FontAwesomeIcon icon={faMagnifyingGlass} className={styles.searchIcon} aria-hidden="true" />
                <input id="org-student-search" className={styles.searchInput} type="search" placeholder="Buscar aluno..." aria-label="Buscar aluno" value={search} onChange={(e) => setSearch(e.target.value)} />
              </div>
              <div className={styles.filters}>
                <button type="button" className={`${styles.chip} ${!unassigned && !profFilter ? styles.chipOn : ""}`} aria-pressed={!unassigned && !profFilter} onClick={() => { setUnassigned(false); setProfFilter(""); }}>Todos</button>
                <button type="button" className={`${styles.chip} ${unassigned ? styles.chipOn : ""}`} aria-pressed={unassigned} onClick={() => { setUnassigned(true); setProfFilter(""); }}>
                  Sem professor {queue > 0 && <span className={styles.chipBadge}>{queue}</span>}
                </button>
                <select
                  id="org-student-prof"
                  className={`${styles.chip} ${styles.chipSelect} ${profFilter ? styles.chipOn : ""}`}
                  aria-label="Filtrar por professor"
                  value={profFilter}
                  onChange={(e) => { setProfFilter(e.target.value); setUnassigned(false); }}
                >
                  <option value="">Professor: todos</option>
                  {activeProfs.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>

              {students.length === 0 && !studentsLoading ? (
                <div className={styles.empty}>
                  {unassigned ? "Nenhum aluno na fila. Todos têm professor responsável." : "Nenhum aluno encontrado."}
                </div>
              ) : (
                <table className={styles.table}>
                  <thead>
                    <tr><th scope="col">Aluno</th><th scope="col">Responsável</th><th scope="col"><span className={styles.srOnly}>Ação</span></th></tr>
                  </thead>
                  <tbody>
                    {students.map((s) => {
                      const resp = s.professor_name
                        ? `Prof. ${s.professor_name}`
                        : <span className={`${styles.pill} ${styles.pillWarn}`}>Sem professor</span>;
                      return (
                        <tr key={s.id}>
                          <td className={styles.tdWho}>
                            <div className={styles.who}>
                              <Avatar user={s} apiUrl={apiUrl} />
                              <div className={styles.whoText}>
                                <div className={styles.name}>{s.name}</div>
                                <div className={`${styles.meta} ${styles.metaMobile}`}>{resp}</div>
                              </div>
                            </div>
                          </td>
                          <td className={styles.tdResp}>{resp}</td>
                          <td className={styles.tdAct}>
                            <button type="button" className={`${styles.btn} ${styles.btnSm}`} onClick={() => setTransferFor(s)} aria-label={`${s.professor_id ? "Trocar responsável de" : "Atribuir professor a"} ${s.name}`}>
                              <FontAwesomeIcon icon={faArrowRightArrowLeft} /> {s.professor_id ? "Trocar" : "Atribuir"}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
              {studentsLoading && <div className={styles.empty}>Carregando...</div>}
              {!studentsLoading && students.length < studentsTotal && (
                <button type="button" className={`${styles.btn} ${styles.btnBlock}`} onClick={() => loadStudents(students.length)}>
                  Carregar mais ({studentsTotal - students.length})
                </button>
              )}
            </div>
          )}
        </div>

        {createOpen && (
          <CreateProfessorSheet isOwner={isOwner} onClose={() => setCreateOpen(false)} onDone={() => { setCreateOpen(false); loadOverview(); }} />
        )}
        {actionsFor && (
          <ProfessorActionsSheet
            prof={actionsFor}
            isOwner={isOwner}
            onClose={() => setActionsFor(null)}
            onDeactivate={() => { setDeactivateFor(actionsFor); setActionsFor(null); }}
            onChanged={() => { setActionsFor(null); refreshAll(); }}
          />
        )}
        {deactivateFor && (
          <DeactivateSheet prof={deactivateFor} onClose={() => setDeactivateFor(null)} onDone={() => { setDeactivateFor(null); refreshAll(); }} />
        )}
        {transferFor && (
          <TransferSheet
            student={transferFor}
            professors={professors}
            meId={me?.id}
            onClose={() => setTransferFor(null)}
            onDone={() => { setTransferFor(null); refreshAll(); }}
          />
        )}
      </div>
    </div>
  );
}
